/** `/login/passkey`: phishing-resistant sign-in for privileged roles (§17.1) via @simplewebauthn/browser. */
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { Button, Field, Input } from '@windrop/ui';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { identityApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';
import { AuthLayout } from './AuthLayout';
import { useNextPath } from './Login';
import { useContinueLogin, useLoginFlow } from './Verify';

export function PasskeyPage() {
  const flow = useLoginFlow();
  const location = useLocation();
  const next = useNextPath();
  const continueLogin = useContinueLogin();
  const [email, setEmail] = useState(
    flow?.email ?? (location.state as { email?: string } | null)?.email ?? '',
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== 'undefined' && browserSupportsWebAuthn();

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const { challengeId, options } = await identityApi.passkeyOptions(
        flow ? { challengeId: flow.challengeId } : { email: email || undefined },
      );
      const assertion = await startAuthentication({
        optionsJSON: options as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      const res = await identityApi.passkeyVerify({ challengeId, assertion });
      continueLogin(
        res,
        flow ?? { challengeId, method: 'MAGIC_LINK', email, expiresAt: '', next },
        flow?.code ?? '',
      );
    } catch (err) {
      setError(isApiError(err) ? err.message : 'The passkey was not accepted. Use the email link instead.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={flow ? 'Confirm with your passkey' : 'Sign in with a passkey'}>
      {!supported ? (
        <div className="wdc-callout" data-tone="warning">
          <p>This browser does not support passkeys. Use the email sign-in link instead.</p>
        </div>
      ) : null}
      {!flow ? (
        <Field label="Work email" hint="Optional: narrows the passkey prompt to your account.">
          <Input preset="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      ) : null}
      {error ? (
        <p role="alert" className="text-error">
          {error}
        </p>
      ) : null}
      <Button
        onClick={run}
        loading={busy}
        loadingLabel="Waiting for your passkey"
        disabled={!supported}
        disabledReason={!supported ? 'Passkeys are not available in this browser.' : undefined}
        block
      >
        Continue with passkey
      </Button>
      <p className="wd-muted">
        <Link to="/login">Use the email link instead</Link>
      </p>
    </AuthLayout>
  );
}
